import React, { useCallback, useContext, useEffect, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { useParams } from 'react-router-dom';
import { makeSafeRequest } from '../../common/makeSafeRequest';
import Preloader from '../../components/common/Preloader';
import { AppInfoContext } from '../../components/contexts/AppInfoContext';
import { AssessmentData, AssessmentScenarioData } from '../../types/assessments';
import {
	BaseData,
	ListForcePackageData,
	Optional,
	SystemSettingsObjectType,
} from '../../types/common';
import { FiltersData, InputData } from '../../types/systems';
import AssessmentPage from './assessments-page/AssessmentsPage';
import {
	assessmentsSettingsTemplate,
	defaultScenarioSettings,
	defaultSettings,
	scenariosSettingsTemplate,
} from './defaultSettings';

const Assessments: React.FC = () => {
	const [filters, setFilters] = useState<FiltersData>();
	const [inputData, setInputData] = useState<InputData>();
	const { assessmentId } = useParams();
	const { currentPid } = useContext(AppInfoContext);
	const [_scenariosData, setScenariosData] = useState<BaseData[] | undefined>();

	const initialize = useCallback(async () => {
		const downloadInputData = async () => {
			const response = await makeSafeRequest<InputData>('scenarios/input-data/', 'GET', {
				params: { pid: currentPid },
			});
			return response;
		};

		const filters = await downloadFilters(currentPid);
		const inpData = await downloadInputData();

		if (!filters || !inpData) {
			return false;
		}

		setFilters(filters);
		setInputData(inpData);
		return true;
	}, [currentPid]);

	const downloadFilters = async (pid: number) => {
		return await makeSafeRequest<FiltersData>('assessments/', 'GET', {
			params: { pid: pid },
		});
	};

	const downloadAssessmentSettings = async (assessmentId: number) => {
		return await makeSafeRequest<AssessmentData>(`assessments/${assessmentId}/`, 'GET', {
			onSuccess: (data) => {
				setScenariosData(data.programming_scenarios);
			},
		});
	};

	const downloadScenarioSettings = async (scenarioId: number) => {
		const response = await makeSafeRequest<AssessmentScenarioData>(
			`scenarios/${scenarioId}/`,
			'GET'
		);
		return response;
	};

	const addAssessment = async (settings: Optional<AssessmentData, 'id'>) => {
		return await makeSafeRequest('assessments/', 'POST', {
			payload: settings,
			params: { pid: currentPid },
			onSuccessMessage: 'Assessment added successfully!',
			onErrorMessage: 'Error adding assessment!',
		});
	};

	const addScenario = async (
		settings: Optional<AssessmentScenarioData, 'id'>,
		silent?: boolean
	) => {
		if (!assessmentId) {
			notifications.show({
				title: 'Error',
				message: 'No assessment selected to add a scenario to.',
				color: 'red',
			});
			return null;
		}
		const { id, ...formattedSettings } = settings;
		return await makeSafeRequest<AssessmentScenarioData>(`scenarios/`, 'POST', {
			payload: formattedSettings,
			params: { pid: currentPid, assessment_id: parseInt(assessmentId) },
			onSuccessMessage: silent ? undefined : 'Scenario added successfully!',
			onErrorMessage: 'Error adding Scenario!',
		});
	};

	const deleteAssessment = async (assessmentId: number) => {
		return await makeSafeRequest<null>(`assessments/${assessmentId}/`, 'DELETE', {
			payload: { pid: currentPid },
			onSuccessMessage: 'Assessment deleted successfully!',
			onErrorMessage: 'Error deleting assessment!',
			onSuccess: (data) => {
				setScenariosData(undefined);
			},
		});
	};

	const deleteScenario = async (scenarioId: number) => {
		return await makeSafeRequest<null>(`scenarios/${scenarioId}/`, 'DELETE', {
			params: { pid: currentPid },
			onSuccessMessage: 'Scenario deleted successfully!',
			onErrorMessage: 'Error deleting Scenario!',
			onSuccess: (data) => {
				setScenariosData((prev) =>
					prev ? prev.filter((element) => element.id !== scenarioId) : undefined
				);
			},
		});
	};

	const saveAssessment = async (settings: Optional<AssessmentData, 'id'>) => {
		return await makeSafeRequest<AssessmentData>(`assessments/${settings.id}/`, 'PUT', {
			payload: settings,
			onSuccessMessage: 'Assessment saved successfully!',
			onErrorMessage: 'Error saving assessment!',
		});
	};

	const saveScenario = async (settings: Optional<AssessmentScenarioData, 'id'>) => {
		const { id, ...formattedSettings } = settings;
		return await makeSafeRequest<AssessmentScenarioData>(`scenarios/${id}/`, 'PUT', {
			payload: formattedSettings,
			params: { pid: currentPid },
			onSuccessMessage: 'Scenario saved successfully!',
			onErrorMessage: 'Error saving Scenario!',
		});
	};

	const duplicateAssessment = async (assessmentId: number) => {
		notifications.show({
			id: 'assessmentDuplicated',
			title: 'Info',
			message: 'Assessment is being duplicated...',
			color: 'blue',
			autoClose: false,
			loading: true,
		});

		const json = await makeSafeRequest<AssessmentData>(
			`assessments/${assessmentId}/duplicate/`,
			'POST',
			{
				onErrorMessage: 'Error duplicating assessment!',
			}
		);

		notifications.hide('assessmentDuplicated');

		if (json) {
			notifications.show({
				title: 'Success',
				message: 'Assessment duplicated successfully!',
				color: 'green',
				autoClose: 5000,
			});
		}

		return json;
	};

	const duplicateScenario = async (scenarioId: number) => {
		return await makeSafeRequest<AssessmentScenarioData>(
			`scenarios/${scenarioId}/duplicate/`,
			'POST',
			{
				params: { pid: currentPid },
				onSuccessMessage: 'Scenario duplicated successfully!',
				onErrorMessage: 'Error duplicating scenario!',
			}
		);
	};

	useEffect(() => {
		if (
			inputData &&
			inputData.force_packages &&
			inputData.geographies &&
			scenariosSettingsTemplate
		) {
			scenariosSettingsTemplate.sections['Configuration'].blue_force_package = {
				...(scenariosSettingsTemplate.sections['Configuration']
					.blue_force_package as SystemSettingsObjectType),
				type: 'select',
				data: (inputData.force_packages as ListForcePackageData[]).map(
					(data: ListForcePackageData) => {
						return {
							value: data.id.toString(),
							label: data.name || data.id.toString(),
						};
					}
				),
			};
			scenariosSettingsTemplate.sections['Configuration'].red_force_package = {
				...(scenariosSettingsTemplate.sections['Configuration']
					.red_force_package as SystemSettingsObjectType),
				type: 'select',
				data: (inputData.force_packages as ListForcePackageData[]).map(
					(data: ListForcePackageData) => {
						return {
							value: data.id.toString(),
							label: data.name || data.id.toString(),
						};
					}
				),
			};
			scenariosSettingsTemplate.sections['Configuration'].geography = {
				type: 'select',
				data: (inputData.geographies as string[]).map((data: string) => {
					return { value: data, label: data };
				}),
			};

			if (inputData.force_packages.length > 1) {
				defaultScenarioSettings.blue_force_package = (
					inputData.force_packages as ListForcePackageData[]
				)[0].id;
				defaultScenarioSettings.red_force_package = (
					inputData.force_packages as ListForcePackageData[]
				)[1].id;
				defaultScenarioSettings.geography = (inputData.geographies as string[])[0];
			}
		}
	}, [inputData]);

	useEffect(() => {
		initialize();
	}, [initialize]);

	return filters && inputData ? (
		<AssessmentPage
			forcePackageData={filters.fps as ListForcePackageData[]}
			assessmentsData={filters.assessments as AssessmentData[]}
			inputData={inputData}
			initializeFilters={initialize}
			downloadAssessmentSettings={downloadAssessmentSettings}
			downloadScenarioSettings={downloadScenarioSettings}
			assessmentSettingsTemplate={assessmentsSettingsTemplate}
			scenarioSettingsTemplate={scenariosSettingsTemplate}
			defaultSettings={defaultSettings}
			addAssessment={addAssessment}
			addScenario={addScenario}
			saveAssessment={saveAssessment}
			saveScenario={saveScenario}
			deleteAssessment={deleteAssessment}
			deleteScenario={deleteScenario}
			duplicateAssessment={duplicateAssessment}
			duplicateScenario={duplicateScenario}
		></AssessmentPage>
	) : (
		<Preloader></Preloader>
	);
};

export default Assessments;
